import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { UserUmanforge, RootState, APPS_NAME, getAppsFromMask } from '@umanforge/common';

import { ActionZoneComponent } from './action_zone.component';
import { FlashmeetingActionTicketComponent, PlaniteamActionTicketComponent } from './action_tickets';
import { UserService } from '../../services/user.service';


interface ActionsUserProps {
  user: UserUmanforge;
  refreshUser: () => Promise<void>;
  needConfirmation: boolean;
  setNeedConfirmation: ( value: boolean ) => void;
}

interface FlashmeetingAction {
  id: string;
  eventId?: string;
  engagementId?: string;
  eventManagerId?: string;
  eventManagerName?: string;
  clientId?: string;
  siteId?: string;
  sectorId?: string;
  title: string;
  author: string;
  createdDate: string;
  engagementDate: string;
  theme: {
    name: string;
    color: string;
  };
  daysLate: number;
  isRecurrent: boolean;
  isShared: boolean;
  isCommon: boolean;
  hierarchy?: {
    client?: string;
    site?: string;
    sector?: string;
    eventManager?: string;
  };
}

interface TaskActionData {
  id: string;
  name: string;
  icon: string;
  idSector?: string;
}

interface LocationData {
  name: string | null;
  idSector?: string;
}

interface PlaniteamAction {
  id: string;
  idPlanning: string;
  location: LocationData | null;
  taskAction: TaskActionData | null;
  status: string;
  dateStart: string;
  dateEnd: string;
  debord: number;
  nbSmallPackage: number;
  nbBigPackage: number;
  recurring: number;
  hierarchy?: {
    client?: string;
    site?: string;
    sector?: string;
    eventManager?: string;
  };
}

export function ActionsUserComponent( { user }: ActionsUserProps ) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } ) as string | null;
  const connectedUserId = useSelector( ( state: RootState ) => { return state.user?.value?.id; } ) as string | undefined;

  /*----------------------------- useState ------------------------------*/
  const [ flashmeetingActions, setFlashmeetingActions ] = useState<FlashmeetingAction[]>( [] );
  const [ planiteamActions, setPlaniteamActions ] = useState<PlaniteamAction[]>( [] );
  const [ isLoadingFlashmeeting, setIsLoadingFlashmeeting ] = useState<boolean>( true );
  const [ isLoadingPlaniteam, setIsLoadingPlaniteam ] = useState<boolean>( true );

  const appsOfUser = getAppsFromMask( user?.appAccess ?? 0 );
  const hasFlashmeetingAccessView = appsOfUser.includes( APPS_NAME.FLASHMEETING );
  const hasPlaniteamAccessView = appsOfUser.includes( APPS_NAME.PLANITEAM );

  /*----------------------------- useEffect -----------------------------*/
  useEffect( () => {
    if ( !user || !user.id ) {return;}
    const apps = getAppsFromMask( user.appAccess ?? 0 );
    const hasFlashmeetingAccess = apps.includes( APPS_NAME.FLASHMEETING );
    const hasPlaniteamAccess = apps.includes( APPS_NAME.PLANITEAM );

    if ( hasFlashmeetingAccess ) {
      refreshFlashmeetingActions();
    } else {
      setFlashmeetingActions( [] );
      setIsLoadingFlashmeeting( false );
    }

    if ( hasPlaniteamAccess ) {
      refreshPlaniteamActions();
    } else {
      setPlaniteamActions( [] );
      setIsLoadingPlaniteam( false );
    }
  }, [ user?.id, user?.appAccess, token, dispatch ] );

  /*------------------------- Functions -------------------------*/
  const refreshFlashmeetingActions = async () => {
    const apps = getAppsFromMask( user?.appAccess ?? 0 );
    if ( !apps.includes( APPS_NAME.FLASHMEETING ) ) {
      setFlashmeetingActions( [] );
      setIsLoadingFlashmeeting( false );
      return;
    }
    if ( !user || !user.id ) {
      setFlashmeetingActions( [] );
      setIsLoadingFlashmeeting( false );
      return;
    }
    setIsLoadingFlashmeeting( true );
    try {
      const fetchedActions = await UserService.getUserActionsFlashmeeting( user.id, token, dispatch );
      setFlashmeetingActions( fetchedActions || [] );
    } catch ( error ) {
      console.error( 'Error fetching flashmeeting actions:', error );
      setFlashmeetingActions( [] );
    } finally {
      setIsLoadingFlashmeeting( false );
    }
  };

  const refreshPlaniteamActions = async () => {
    const apps = getAppsFromMask( user?.appAccess ?? 0 );
    if ( !apps.includes( APPS_NAME.PLANITEAM ) ) {
      setPlaniteamActions( [] );
      setIsLoadingPlaniteam( false );
      return;
    }
    if ( !user || !user.id ) {
      setPlaniteamActions( [] );
      setIsLoadingPlaniteam( false );
      return;
    }
    setIsLoadingPlaniteam( true );
    try {
      const fetchedActions = await UserService.getUserActionsPlaniteam( user.id, token, dispatch );
      setPlaniteamActions( fetchedActions || [] );
    } catch ( error ) {
      console.error( 'Error fetching planiteam actions:', error );
      setPlaniteamActions( [] );
    } finally {
      setIsLoadingPlaniteam( false );
    }
  };

  /*------------------------- Front --------------------------*/
  return (
    <div className='relative flex flex-col h-full w-full items-center justify-center'>
      <div className='flex flex-col w-full h-full items-center max-w-[1500px] min-w-[400px] gap-10 px-10 mb-[63px] mt-4'>

        {/* Flashmeeting Actions Zone */}
        { hasFlashmeetingAccessView ? (
          <ActionZoneComponent
            title={ t( 'flashmeeting_actions' ) }
            color='#fa4616'
            isLoading={ isLoadingFlashmeeting }
            isEmpty={ flashmeetingActions.length === 0 }
            showActionButton={ connectedUserId === user?.id }
          >
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-max'>
              {
                flashmeetingActions.map( action => {return (
                  <FlashmeetingActionTicketComponent
                    key={ action.id }
                    id={ action.id }
                    eventId={ action.eventId }
                    engagementId={ action.engagementId }
                    eventManagerId={ action.eventManagerId }
                    eventManagerName={ action.eventManagerName }
                    clientId={ action.clientId }
                    siteId={ action.siteId }
                    sectorId={ action.sectorId }
                    title={ action.title }
                    author={ action.author }
                    createdDate={ action.createdDate }
                    engagementDate={ action.engagementDate }
                    theme={ action.theme }
                    daysLate={ action.daysLate }
                    isRecurrent={ action.isRecurrent }
                    isShared={ action.isShared }
                    isCommon={ action.isCommon }
                    hierarchy={ action.hierarchy }
                  />
                );} )
              }
            </div>
          </ActionZoneComponent>
        ) : null }

        { !hasFlashmeetingAccessView && !hasPlaniteamAccessView ? (
          <div className='w-full flex items-center justify-center py-12'>
            <span className='text-gray-600 text-lg'>
              { t( 'no_actions_available' ) }
            </span>
          </div>
        ) : null }

        {/* Planiteam Actions Zone */}
        { hasPlaniteamAccessView ? (
          <ActionZoneComponent
            title={ t( 'planiteam_actions' ) }
            color='#3b82f6'
            isLoading={ isLoadingPlaniteam }
            isEmpty={ planiteamActions.length === 0 }
            showActionButton={ connectedUserId === user?.id }
          >
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-max'>
              {
                planiteamActions.map( action => {return (
                  <PlaniteamActionTicketComponent
                    key={ action.id }
                    id={ action.id }
                    missionTitle={ action.taskAction?.name || 'N/A' }
                    missionIcon={ action.taskAction?.icon }
                    location={ action.location?.name }
                    startDate={ action.dateStart }
                    endDate={ action.dateEnd }
                    hierarchy={ action.hierarchy }
                    sectorId={ action.taskAction?.idSector ?? action.location?.idSector }
                  />
                );} )
              }
            </div>
          </ActionZoneComponent>
        ) : null }

      </div>
    </div>
  );
}

